import React from 'react';
import ReactSelect, { Props as ReactSelectProps } from 'react-select';

import { palette } from '../theme';
import { modularScale, pxToRem } from '../utils';

interface IOption<V> {
  value: V;
  label: string;
}

type StyleFunction<C, P> = (base: C, props: P) => C;

function composeStyles<C, P>(
  ...fns: (StyleFunction<C, P> | undefined)[]
): StyleFunction<C, P> {
  return (base, props) =>
    fns.reduce<C>((acc, fn) => (fn ? fn(acc, props) : acc), base);
}
interface ISelectProps<V, M extends boolean = false>
  extends ReactSelectProps<IOption<V>, M> {
  relativePosition?: boolean;
}

/**
 *
 * ```tsx
 *
 * import { SelectField } from '@ottatech/design';
 *
 * ```
 */

export function SelectField<V, M extends boolean = false>({
  relativePosition,
  styles,
  ...props
}: ISelectProps<V, M>): React.ReactElement {
  return (
    <ReactSelect
      styles={{
        ...styles,
        menu: composeStyles(styles?.menu, (base) =>
          relativePosition ? { ...base, position: 'relative' } : base
        ),
        multiValue: composeStyles(styles?.multiValue, (base) => ({
          ...base,
          borderRadius: pxToRem(4),
          border: `${pxToRem(1)} solid ${palette.grayscale.shade600}`,
        })),
        multiValueRemove: composeStyles(styles?.multiValueRemove, (base) => ({
          ...base,
          borderRadius: pxToRem(4),
          cursor: 'pointer',
        })),
        multiValueLabel: composeStyles(styles?.multiValueLabel, (base) => ({
          ...base,
          background: 'white',
          padding: `${modularScale(-8)} ${modularScale(-3)}`,
          paddingLeft: modularScale(-4),
          borderRadius: `${pxToRem(4)} 0 0 ${pxToRem(4)}`,
        })),
        valueContainer: composeStyles(styles?.valueContainer, (base) => ({
          ...base,
          padding: `${modularScale(-8)} ${modularScale(-7)}`,
        })),
        control: composeStyles(styles?.control, (base) => ({
          ...base,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          ':active': {
            border: `${pxToRem(1)} solid ${palette.brand.black}`,
          },
        })),
      }}
      theme={(originalTheme) => ({
        ...originalTheme,
        borderRadius: 4,
        colors: {
          ...originalTheme.colors,
          primary: palette.brand.black,
          primary25: palette.brand.grey,
          danger: palette.brand.white,
          dangerLight: palette.grayscale.shade800,
        },
      })}
      {...props}
    />
  );
}
